import React from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent } from '~/components/ui/dialog';
import {
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '~/components/ui/table';
import Button from '~/modules/components/global/button';
import EmptyState from '~/modules/components/global/empty-state';
import ToastService from '~/modules/services/toast-service';
import WebhookEndpointService from '~/modules/services/webhook-endpoint-service';
import { captureError } from '../../../pixwel/exception-handler.factory';

const formatDate = (timestamp) => {
	if(!timestamp) {
		return '—';
	}
	return new Date(timestamp * 1000).toLocaleString();
};

const STATUS_CLASSES = {
	succeeded: 'bg-green-500/15 text-green-400',
	failed: 'bg-red-500/15 text-red-400',
	pending_retry: 'bg-orange-500/15 text-orange-400',
	pending: 'bg-blue-500/15 text-blue-400'
};

function StatusPill({ status }) {
	return (
		<span className={`rounded-xs px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[status] || 'bg-muted text-muted-foreground'}`}>
			{status}
		</span>
	);
}

StatusPill.propTypes = {
	status: PropTypes.string
};

const WebhookDeliveriesModal = ({ endpoint, onClose }) => {
	const queryClient = useQueryClient();
	const endpointId = endpoint?._id;
	const queryKey = ['webhookDeliveries', endpointId];

	const { data: deliveries = [], isLoading } = useQuery(
		queryKey,
		() => WebhookEndpointService.deliveries(endpointId),
		{ enabled: !!endpointId, refetchOnWindowFocus: false }
	);

	const { mutateAsync: retry } = useMutation(
		(id) => WebhookEndpointService.retryDelivery(id),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(queryKey);
				ToastService.create('success', 'Delivery re-queued');
			},
			onError: (err) => {
				ToastService.create('error', err?.response?.data?.error || 'Failed to retry delivery');
				captureError(err, `Error retrying webhook delivery: ${err.message}`);
			}
		}
	);

	return (
		<Dialog open={!!endpoint} onOpenChange={(open) => {
			if(!open) {
				onClose();
			}
		}}
		>
			<DialogContent
				className="rounded-xl p-0 overflow-hidden flex flex-col"
				style={{ maxWidth: '48rem', maxHeight: '80vh' }}
			>
				<div className="px-6 pt-6 pb-4 border-b border-border shrink-0">
					<h3 className="m-0 text-lg font-semibold">Recent deliveries</h3>
					<p className="mt-1 mb-0 text-sm text-muted-foreground break-all">
						<code>{endpoint?.url}</code>
					</p>
				</div>
				<div className="p-6 overflow-y-auto">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Event</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Attempts</TableHead>
								<TableHead>HTTP</TableHead>
								<TableHead>Last attempt</TableHead>
								<TableHead></TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{isLoading
								? (
									<TableRow>
										<TableCell colSpan={6} className="text-center text-muted-foreground">Loading…</TableCell>
									</TableRow>
								)
								: deliveries.length === 0
									? (
										<TableRow>
											<TableCell colSpan={6}>
												<EmptyState message="No deliveries yet" />
											</TableCell>
										</TableRow>
									)
									: (
										deliveries.map((delivery) => (
											<TableRow key={delivery._id}>
												<TableCell className="max-w-[12rem] truncate font-medium">{delivery.eventType}</TableCell>
												<TableCell><StatusPill status={delivery.status} /></TableCell>
												<TableCell>{delivery.attempts}</TableCell>
												<TableCell>{delivery.responseStatus || '—'}</TableCell>
												<TableCell className="text-muted-foreground text-sm">
													{formatDate(delivery.lastAttemptAt)}
												</TableCell>
												<TableCell className="text-right">
													<Button
														variant="secondary"
														data-testid="webhook-delivery-retry-button"
														onClick={() => retry(delivery._id)}
													>
														Resend
													</Button>
												</TableCell>
											</TableRow>
										))
									)}
						</TableBody>
					</Table>
				</div>
				<div className="flex justify-end border-t border-border px-6 py-4 shrink-0">
					<Button onClick={onClose} data-testid="webhook-deliveries-close-button">
						Close
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

WebhookDeliveriesModal.propTypes = {
	endpoint: PropTypes.shape({
		_id: PropTypes.string,
		url: PropTypes.string
	}),
	onClose: PropTypes.func.isRequired
};

export default WebhookDeliveriesModal;
